"use client"

import type { AlbumImage, AlbumLayout } from "@/stores/use-album-modal-store"
import { DragEvent, useEffect, useState } from "react"

interface AlbumPreviewProps {
  images: AlbumImage[]
  layout: AlbumLayout
  onReorder?: (images: AlbumImage[]) => void
}

type ColumnDef = { width: number; stack: number }
type RowPattern = { columns: ColumnDef[] }

const DEFAULT_MASONRY_PATTERN: RowPattern[] = [
  { columns: [{ width: 50, stack: 1 }, { width: 20, stack: 2 }, { width: 30, stack: 1 }] },
  { columns: [{ width: 33.34, stack: 1 }, { width: 33.33, stack: 1 }, { width: 33.33, stack: 1 }] },
  { columns: [{ width: 50, stack: 1 }, { width: 50, stack: 1 }] },
]

export default function AlbumPreview({ images, layout, onReorder }: AlbumPreviewProps) {
  const [localImages, setLocalImages] = useState(images)
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)

  useEffect(() => {
    setLocalImages(images)
  }, [images])

  const handleThumbDragStart = (e: DragEvent, index: number) => {
    setDraggedIndex(index)
    e.dataTransfer.effectAllowed = "move"
      ; (e.currentTarget as HTMLElement).style.opacity = "0.5"
  }

  const handleThumbDragEnd = (e: DragEvent) => {
    setDraggedIndex(null)
      ; (e.currentTarget as HTMLElement).style.opacity = "1"
  }

  const handleThumbDragOver = (e: DragEvent, targetIndex: number) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = "move"

    if (draggedIndex === null || draggedIndex === targetIndex) return

    const newImages = [...localImages]
    const [dragged] = newImages.splice(draggedIndex, 1)
    newImages.splice(targetIndex, 0, dragged)

    setLocalImages(newImages)
    onReorder?.(newImages)
    setDraggedIndex(targetIndex)
  }

  const getGridCols = (count: number) => (count <= 2 ? count : count <= 6 ? 3 : 4)

  const renderGridLayout = () => {
    const cols = getGridCols(localImages.length)
    return (
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: "8px" }}>
        {localImages.map((img, i) => (
          <div key={img.id} className="relative" style={{ aspectRatio: "1" }}>
            <img
              src={img.display || img.url}
              alt={img.alt || img.name}
              style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "4px" }}
            />
            <span className="absolute top-1 left-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
              {i + 1}
            </span>
          </div>
        ))}
      </div>
    )
  }

  const renderMasonryLayout = () => {
    let queue = [...localImages]
    const rows: { columns: { width: number; imgs: AlbumImage[] }[] }[] = []
    let patternIndex = 0
    const rowHeight = 260
    const gap = 8

    while (queue.length > 0) {
      const pattern = DEFAULT_MASONRY_PATTERN[patternIndex % DEFAULT_MASONRY_PATTERN.length]
      patternIndex++

      const row: { columns: { width: number; imgs: AlbumImage[] }[] } = { columns: [] }
      let usedAny = false

      for (const col of pattern.columns) {
        if (queue.length === 0) break
        const stackImgs = queue.splice(0, col.stack)
        if (stackImgs.length === 0) continue
        usedAny = true
        row.columns.push({ width: col.width, imgs: stackImgs })
      }

      if (!usedAny) break
      rows.push(row)
    }

    return (
      <div className="ck-album-masonry" style={{ display: "flex", flexDirection: "column", gap: `${gap}px` }}>
        {rows.map((row, ri) => (
          <div
            key={ri}
            style={{
              display: "flex",
              width: "100%",
              height: `${rowHeight}px`,
              gap: `${gap}px`,
            }}
          >
            {row.columns.map((col, ci) => (
              <div
                key={ci}
                style={{
                  width: `${col.width}%`,
                  height: "100%",
                  display: "flex",
                  flexDirection: col.imgs.length > 1 ? "column" : "row",
                  gap: col.imgs.length > 1 ? `${gap}px` : "0",
                  overflow: "hidden",
                }}
              >
                {col.imgs.map((img, si) => {
                  const idx = localImages.indexOf(img)
                  return (
                    <div
                      key={img.id}
                      style={{
                        flex: col.imgs.length > 1 ? "1 1 0" : "1",
                        overflow: "hidden",
                        borderRadius: "8px",
                        position: "relative",
                      }}
                    >
                      <img
                        src={img.display || img.url}
                        alt={img.alt || img.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                          transition: "transform 0.3s ease",
                        }}
                      />
                      <span className="absolute top-1 left-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
                        {idx + 1}
                      </span>
                    </div>
                  )
                })}
              </div>
            ))}
          </div>
        ))}
      </div>
    )
  }

  const renderSliderLayout = () => (
    <div
      style={{
        position: "relative",
        overflow: "hidden",
        borderRadius: "8px",
        width: "100%",
        aspectRatio: "16/9",
        background: "#f3f4f6",
      }}
    >
      {localImages[0] && (
        <img
          src={localImages[0].display || localImages[0].url}
          alt={localImages[0].alt || localImages[0].name}
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      )}
      <span className="absolute top-2 left-2 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
        1/{localImages.length}
      </span>
    </div>
  )

  const handleDelete = (index: number) => {
    const newImages = localImages.filter((_, i) => i !== index)
    setLocalImages(newImages)
    onReorder?.(newImages)
  }

  return (
    <div>
      <div className="flex items-center justify-between px-3 py-2 bg-gray-100 rounded-lg mb-3">
        <span className="text-xs text-gray-600 flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
          </svg>
          Xem trước album
        </span>
        <span className="text-xs text-gray-500">{localImages.length} ảnh</span>
      </div>

      {/* Actual album preview */}
      <div className="min-h-[280px] border border-dashed border-gray-300 rounded-lg p-3 bg-gray-50/50 overflow-auto">
        {layout === "grid" && renderGridLayout()}
        {layout === "masonry" && renderMasonryLayout()}
        {layout === "slider" && renderSliderLayout()}
      </div>

      {/* Draggable thumbnails for reordering */}
      <div className="flex items-start gap-2 mt-3 pt-3 border-t border-gray-200">
        <span className="text-xs text-gray-500 shrink-0 pt-1">Sắp xếp:</span>
        <div className="flex flex-wrap gap-2">
          {localImages.map((img, index) => (
            <div
              key={img.id}
              draggable
              onDragStart={(e) => handleThumbDragStart(e, index)}
              onDragEnd={handleThumbDragEnd}
              onDragOver={(e) => {
                e.preventDefault()
                handleThumbDragOver(e, index)
              }}
              className={`
                w-14 h-14 rounded-lg overflow-hidden cursor-grab active:cursor-grabbing
                transition-all duration-150 relative group
                ${draggedIndex === index ? "ring-2 ring-blue-500 scale-110 z-10 shadow-lg" : "hover:ring-2 hover:ring-blue-300 hover:scale-105"}
              `}
            >
              <img
                src={img.thumbnail || img.url}
                alt={img.alt || img.name}
                className="w-full h-full object-cover pointer-events-none"
                draggable={false}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="text-white text-[10px] font-medium block text-center">
                  #{index + 1}
                </span>
              </div>
              {/* Delete button */}
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleDelete(index)
                }}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md z-20"
                title="Xóa khỏi album"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
